import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { DealTrackerLead, DealStage } from "@/types/dealTracker";
import { useToast } from "@/hooks/use-toast";

export const useDealTracker = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all leads for current seller
  const { data: leads = [], isLoading, error } = useQuery({
    queryKey: ['deal-tracker-leads', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('deal_tracker_leads')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as DealTrackerLead[];
    },
    enabled: !!user?.id,
  });

  // Create new lead
  const createLead = useMutation({
    mutationFn: async (leadData: Omit<DealTrackerLead, 'id' | 'seller_id' | 'created_at' | 'updated_at'>) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('deal_tracker_leads')
        .insert({
          ...leadData,
          seller_id: user.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tracker-leads'] });
      toast({
        title: "Lead Created",
        description: "Your new lead has been added to the tracker.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create lead. Please try again.",
        variant: "destructive",
      });
      console.error('Create lead error:', error);
    },
  });

  // Update lead
  const updateLead = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DealTrackerLead> & { id: string }) => {
      const { data, error } = await supabase
        .from('deal_tracker_leads')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tracker-leads'] });
      toast({
        title: "Lead Updated",
        description: "Lead information has been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update lead. Please try again.",
        variant: "destructive",
      });
      console.error('Update lead error:', error);
    },
  });

  // Update deal stage only (quick update)
  const updateStage = useMutation({
    mutationFn: async ({ id, deal_stage }: { id: string; deal_stage: DealStage }) => {
      const { data, error } = await supabase
        .from('deal_tracker_leads')
        .update({ deal_stage })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tracker-leads'] });
      toast({
        title: "Stage Updated",
        description: "Deal stage has been updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update stage. Please try again.",
        variant: "destructive",
      });
      console.error('Update stage error:', error);
    },
  });

  // Delete lead
  const deleteLead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('deal_tracker_leads')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-tracker-leads'] });
      toast({
        title: "Lead Deleted",
        description: "The lead has been removed from your tracker.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete lead. Please try again.",
        variant: "destructive",
      });
      console.error('Delete lead error:', error);
    },
  });

  // Get counts by stage
  const stageCounts = leads.reduce((acc, lead) => {
    acc[lead.deal_stage] = (acc[lead.deal_stage] || 0) + 1;
    return acc;
  }, {} as Record<DealStage, number>);

  return {
    leads,
    isLoading,
    error,
    createLead,
    updateLead,
    updateStage,
    deleteLead,
    stageCounts,
  };
};
